/* eslint camelcase: "off" */
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Tag } from '@jem-open/jem-ui';
import { Loader2 } from 'lucide-react';

import apiResponseHandler from '../../../../utils/apiResponseHandler';
import {
  FraudSeverity,
  IdentityVerificationListItem,
  verificationClient,
} from '../../../../services/verificationClient';
import { severityTagVariant } from '../../Security/components/identityVerifications/severity';


interface Props {
  employeeId: number;
}


const REVIEW_OUTCOME_VARIANT: Record<string, 'success' | 'failed' | 'pending' | 'processing'> = {
  confirm_fraud: 'failed',
  cleared: 'success',
  accepted_with_override: 'processing',
  recapture_required: 'pending',
  inconclusive: 'pending',
  escalate: 'failed',
};


/**
 * Per-employee verification timeline — every ID-photo / selfie capture
 * attempt the employee has gone through, in reverse-chronological order.
 *
 * Wired to verificationClient.listIdentityVerifications with
 * doc_types="id_photo,selfie" — the django endpoint defaults to
 * id_photo for the fraud queue, but we opt into the broader set here so
 * the timeline shows the whole capture trail.
 */
const EmployeeIdentificationDocumentsSection: React.FC<Props> = ({ employeeId }) => {
  const history = useHistory();
  const [rows, setRows] = useState<IdentityVerificationListItem[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setRows([]);
    try {
      const page = await verificationClient.listIdentityVerifications({
        employee_id: employeeId,
        doc_types: 'id_photo,selfie',
      });
      setRows(page.results);
    } catch (err) {
      apiResponseHandler.handleApiError(err);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    load();
  }, [load]);

  const events: TimelineRow[] = rows.map(adaptWireRow);

  return (
    <div className="rounded-2xl border border-greyscale-border-default bg-white p-md">
      <h4 className="mb-sm text-sm font-semibold">Verification timeline</h4>
      {events.length === 0 && !loading ? (
        <div className="text-xs text-greyscale-text-caption">
          No verification events recorded.
        </div>
      ) : (
        <div className="flex flex-col gap-xs">
          {events.map((e) => (
            <div
              key={e.id}
              className="flex items-center gap-sm rounded-lg bg-greyscale-surface-subtle p-xs"
            >
              <div className="w-40 text-xxs text-greyscale-text-caption">
                {new Date(e.captured_at).toLocaleString()}
              </div>
              <div className="flex-1">
                <div className="text-xs">
                  Flow session{' '}
                  {e.flow_session_id ? (
                    <button
                      className="underline text-primary-text-label"
                      onClick={() =>
                        history.push(`/admin/security/sessions/${e.flow_session_id}`)
                      }
                    >
                      {e.flow_session_id}
                    </button>
                  ) : (
                    <span className="text-greyscale-text-caption">—</span>
                  )}
                </div>
                <div className="text-xxs text-greyscale-text-caption">
                  {e.document_kind || e.doc_type} · attempt #{e.attempt_no ?? '—'} · method{' '}
                  {e.extraction_method || '—'}
                </div>
              </div>
              <Tag
                variant={
                  e.verification_status === 'mismatch'
                    ? 'failed'
                    : e.verification_status === 'ocr_passed'
                    ? 'success'
                    : 'pending'
                }
              >
                {e.verification_status}
              </Tag>
              {e.fraud_severity && (
                <Tag variant={severityTagVariant(e.fraud_severity)}>{e.fraud_severity}</Tag>
              )}
              {e.review_outcome && (
                <Tag variant={REVIEW_OUTCOME_VARIANT[e.review_outcome] || 'pending'}>
                  {e.review_outcome.replace(/_/g, ' ')}
                </Tag>
              )}
              {e.reviewer_note && (
                <div
                  className="ml-xxs max-w-xs truncate text-xxs italic text-greyscale-text-caption"
                  title={e.reviewer_note}
                >
                  &quot;{e.reviewer_note}&quot;
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {loading && (
        <div className="mt-sm flex justify-center">
          <Loader2 className="animate-spin text-secondary-text-label" size={20} />
        </div>
      )}
    </div>
  );
};


// Local timeline row shape — minimum the JSX needs. Lets us render both
// wire rows and mock rows through the same map() without type gymnastics.
interface TimelineRow {
  id: string;
  captured_at: string;
  flow_session_id: string | null;
  doc_type: string;
  document_kind: string | null;
  attempt_no: number | null;
  extraction_method: string | null;
  verification_status: string;
  fraud_severity: FraudSeverity | null | undefined;
  reviewer_note: string | null | undefined;
  review_outcome: string | null | undefined;
}

function adaptWireRow(r: IdentityVerificationListItem): TimelineRow {
  return {
    id: r.id,
    captured_at: r.captured_at,
    flow_session_id: r.flow_session_id,
    doc_type: r.doc_type,
    document_kind: r.document_kind,
    attempt_no: r.attempt_no,
    extraction_method: r.extraction_method,
    verification_status: r.verification_status,
    fraud_severity: r.fraud_severity,
    reviewer_note: r.reviewer_note,
    review_outcome: r.review_outcome,
  };
}

export default EmployeeIdentificationDocumentsSection;
